import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, MapPin, Phone, ShieldCheck, Clock, ExternalLink, MessageCircle, Send, CheckCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import {
  STORE_ADDRESS_LINES,
  STORE_EMAIL,
  STORE_EMAIL_HREF,
  STORE_HOURS,
  STORE_MAP_EMBED,
  STORE_MAPS_LINK,
  STORE_PHONE,
  STORE_PHONE_HREF,
  STORE_WHATSAPP,
} from "../lib/storeInfo";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/contact", form);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="bg-navy-700 rounded-2xl p-8 neon-border flex flex-col items-center justify-center text-center gap-4 min-h-[320px]">
        <CheckCircle className="w-12 h-12 text-gold" />
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Message Sent</div>
        <h3 className="text-2xl font-black uppercase">We'll get back to you soon!</h3>
        <p className="text-sm text-slate-400 max-w-xs">Thanks for reaching out. The GamesTrade team will reply to your email shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-navy-700 rounded-2xl p-6 lg:p-8 neon-border flex flex-col gap-4">
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-1">Send us a message</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Name</label>
          <input
            name="name" required value={form.name} onChange={handleChange}
            placeholder="Your name"
            className="bg-navy-900 border border-gold/20 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60 transition"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Phone</label>
          <input
            name="phone" required value={form.phone} onChange={handleChange}
            placeholder="+91 98765 43210"
            className="bg-navy-900 border border-gold/20 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60 transition"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Email</label>
        <input
          name="email" required type="email" value={form.email} onChange={handleChange}
          placeholder="you@example.com"
          className="bg-navy-900 border border-gold/20 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60 transition"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Message</label>
        <textarea
          name="message" required rows={4} value={form.message} onChange={handleChange}
          placeholder="How can we help you?"
          className="bg-navy-900 border border-gold/20 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60 transition resize-none"
        />
      </div>
      {status === "error" && <p className="text-sm text-red-400">Something went wrong. Please try again or email us directly.</p>}
      <button
        type="submit" disabled={status === "loading"}
        className="mt-1 self-start inline-flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all disabled:opacity-60"
      >
        <Send className="w-4 h-4" /> {status === "loading" ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
}

export default function ContactPage() {
  return (
    <div>
      <Helmet>
        <title>{"Contact Us - GamesTrade Noida"}</title>
        <meta name="description" content="Contact GamesTrade Noida for orders, support, repairs, trade-ins and store visits. Email, call, WhatsApp or visit us at Spectrum Mall, Sector 75, Noida." />
        <link rel="canonical" href="https://gamestrade.in/contact" />
        <meta property="og:title" content="Contact GamesTrade Noida" />
        <meta property="og:description" content="Email, call, WhatsApp or visit GamesTrade at Spectrum Mall, Sector 75, Noida." />
        <meta property="og:url" content="https://gamestrade.in/contact" />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageHero
        eyebrow="Contact"
        title={<>Need help? <br /><span className="text-gold">Talk to GamesTrade.</span></>}
        description="For product support, order help, repairs, trade-ins or store visits, reach us directly or drop by the Noida flagship store."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Contact" }]}
      />

      <section className="site-container py-16 flex flex-col gap-10">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-6 items-stretch">
          <div className="space-y-4">
            <a href={STORE_EMAIL_HREF} className="block bg-navy-700 rounded-2xl p-6 neon-border hover:bg-navy-600 transition">
              <Mail className="w-8 h-8 text-gold mb-4" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Email Support</div>
              <h2 className="text-2xl font-black tracking-tighter uppercase">Write to us</h2>
              <p className="text-sm text-slate-300 mt-2 break-all">{STORE_EMAIL}</p>
            </a>

            <a href={STORE_PHONE_HREF} className="block bg-navy-700 rounded-2xl p-6 neon-border hover:bg-navy-600 transition">
              <Phone className="w-8 h-8 text-gold mb-4" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Call</div>
              <h2 className="text-2xl font-black tracking-tighter uppercase">{STORE_PHONE}</h2>
              <p className="text-sm text-slate-300 mt-2">{STORE_HOURS}</p>
            </a>

            <a href={STORE_WHATSAPP} target="_blank" rel="noopener noreferrer" className="block bg-navy-700 rounded-2xl p-6 neon-border hover:bg-navy-600 transition">
              <MessageCircle className="w-8 h-8 text-gold mb-4" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">WhatsApp</div>
              <h2 className="text-2xl font-black tracking-tighter uppercase">Quick support</h2>
              <p className="text-sm text-slate-300 mt-2">Fast help for availability, orders, repairs and trade-in quotes.</p>
            </a>
          </div>

          <div className="bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl border border-gold/30 overflow-hidden neon-border flex flex-col">
            <div className="p-6 lg:p-8">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Store Location</div>
              <h2 className="text-3xl font-black tracking-tighter uppercase">GamesTrade Noida</h2>
              <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm text-slate-300">
                <a href={STORE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-gold transition">
                  <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <span>{STORE_ADDRESS_LINES.map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}</span>
                </a>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <span>{STORE_HOURS}</span>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <span>Sony India Authorised</span>
                </div>
              </div>
              <a href={STORE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-6 py-3 rounded-full hover:bg-gold-light btn-glow transition-all">
                Open in Google Maps <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="min-h-[360px] flex-1 border-t border-gold/20 bg-navy-800">
              <iframe
                title="GamesTrade Noida Google Map"
                src={STORE_MAP_EMBED}
                className="w-full h-full min-h-[360px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
        <ContactForm />
      </section>
    </div>
  );
}
